/**
 * Content Hub picker — choose one of the team's own medias (creator videos,
 * images, stories) for a slide. Same data as the Content Hub page: the paths
 * tree (brand → campaign → creator) on the left, the search gallery on the
 * right, both cached for the session through React Query so reopening the
 * picker is instant. Uses the user's Beatly JWT, so clients only ever see
 * their own content.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useInfiniteQuery, useQuery, type QueryClient } from '@tanstack/react-query';
import { X, Search, Loader2, Play, ChevronRight, ChevronDown, Folder, Film, Sparkles, Upload, Users, BadgeCheck } from 'lucide-react';
import { getBoContentHubSearch, getContentHubPaths, getContentHubSearch, type ContentHubMedia, type ContentHubMediaPage, type ContentHubPathRow } from '@/api/beatlyApi';
import { getTokenFromStorage } from '@/utils/auth';
import { isAdminJwtToken } from '@/utils/jwtTokenType';
import { getStaffScopeTeam, isClientTeamId } from '@/cowork/staffScope';
import { coworkApi, API_BASE_URL, type CreatorFacts } from '@/cowork/omniad/api/coworkApi';
import { CONTENT_HUB_QUERY_PERSISTENCE } from '@/utils/contentHubPrefetch';

export interface SlideMedia {
  url: string;
  alt?: string;
  kind?: 'image' | 'video';
  posterUrl?: string;
  sourceId?: string;
  href?: string;
  caption?: string;
  /** Vertical creator content: '9:16' reels/stories/TikToks, '4:5' feed posts. */
  aspect?: '9:16' | '4:5' | '1:1' | '16:9';
  /** 'phone' draws a bezel around vertical content. */
  frame?: 'phone';
}

const VIDEO_HINTS = ['VIDEO', 'REEL', 'TIKTOK', 'YOUTUBE', 'SHORT'];
const PAGE_SIZE = 24;

export function mediaFromContentHub(m: ContentHubMedia): SlideMedia {
  const type = String(m.product_type || '').toUpperCase();
  const name = String(m.filename || m.contenthub_url || '').toLowerCase();
  const isVideo = VIDEO_HINTS.some((h) => type.includes(h)) || /\.(mp4|mov|m4v|webm)(\?|$)/.test(name);
  const aspect = '9:16' as const; // everything is shown phone-sized (user preference)
  return {
    url: m.contenthub_url,
    posterUrl: m.preview_url || thumbnailFor(m.contenthub_url) || undefined,
    kind: isVideo ? 'video' : 'image',
    ...(aspect ? { aspect } : {}),
    href: m.contenthub_url,
    sourceId: m.media_id,
    alt: [m.influencer_name || m.influencer_handle, m.campaign_name].filter(Boolean).join(' · ') || undefined,
  };
}

/** A still for a video URL without a preview: Cloudinary first frame, else the CDN's .jpg sibling. Never a <video> element. */
function thumbnailFor(url: string): string | null {
  if (!url) return null;
  const [base, query] = url.split('?');
  if (!/\.(mp4|mov|avi|webm|ogg|m4v)$/i.test(base)) return null;
  if (base.includes('res.cloudinary.com') && base.includes('/video/upload/')) return base.replace('/video/upload/', '/video/upload/so_0/').replace(/\.(mp4|mov|avi|webm|ogg|m4v)$/i, '.jpg');
  const jpg = base.replace(/\.(mp4|mov|avi|webm|ogg|m4v)$/i, '.jpg');
  return query ? `${jpg}?${query}` : jpg;
}

async function fetchPaths(): Promise<ContentHubPathRow[]> {
  const token = getTokenFromStorage();
  if (!token) throw new Error('No auth token');
  const res = await getContentHubPaths(token);
  return res.folders ?? [];
}

/** Warm the tree and the first gallery page (called when the editor opens). */
export function prefetchContentHubPicker(qc: QueryClient, campaignId?: string | null): void {
  void qc.prefetchQuery({ queryKey: ['contenthub', 'paths'], queryFn: fetchPaths, ...CONTENT_HUB_QUERY_PERSISTENCE });
  const token = getTokenFromStorage();
  if (!token) return;
  void qc.prefetchInfiniteQuery({
    queryKey: ['contenthub', 'picker', campaignId || null, null, ''],
    queryFn: () => getContentHubSearch(token, { pageNumber: 0, pageSize: PAGE_SIZE, campaignId: campaignId || null }),
    initialPageParam: 0,
    ...CONTENT_HUB_QUERY_PERSISTENCE,
  });
}

interface CreatorNode { id: string; name: string; handle: string; avatarUrl?: string }
interface CampaignNode { id: string; name: string; year: number | null; creators: CreatorNode[] }
interface BrandNode { id: string; name: string; logoUrl?: string; campaigns: CampaignNode[] }

function buildTree(rows: ContentHubPathRow[]): BrandNode[] {
  const brands = new Map<string, BrandNode & { camps: Map<string, CampaignNode & { creators: Map<string, CreatorNode> }> }>();
  for (const r of rows) {
    if (!r.brand_id || !r.campaign_id) continue;
    const b = brands.get(r.brand_id) ?? { id: r.brand_id, name: r.brand_name || 'Brand', logoUrl: r.brand_logo_url, campaigns: [], camps: new Map() };
    const c = b.camps.get(r.campaign_id) ?? { id: r.campaign_id, name: r.campaign_name || r.campaign_id, year: r.campaign_year ?? null, creators: new Map() } as CampaignNode & { creators: Map<string, CreatorNode> };
    if (c.year == null && r.campaign_year != null) c.year = r.campaign_year;
    if (r.collaboration_id) c.creators.set(r.collaboration_id, { id: r.collaboration_id, name: r.influencer_name || r.influencer_handle || 'Creator', handle: r.influencer_handle || '', avatarUrl: r.influencer_mugshot_url });
    b.camps.set(r.campaign_id, c);
    brands.set(r.brand_id, b);
  }
  return [...brands.values()].map((b) => ({
    id: b.id, name: b.name, logoUrl: b.logoUrl,
    campaigns: [...b.camps.values()].map((c) => ({ id: c.id, name: c.name, year: c.year, creators: [...c.creators.values()].sort((a, z) => a.name.localeCompare(z.name)) }))
      .sort((a, z) => (z.year ?? 0) - (a.year ?? 0) || a.name.localeCompare(z.name)),
  })).sort((a, z) => a.name.localeCompare(z.name));
}

interface CreatorHit { id?: string; url: string; name: string; handle: string; platform?: 'INSTAGRAM' | 'TIKTOK' | string; followers?: number; verified?: boolean; source: 'campaign' | 'lookup' | 'database'; campaign?: string }

function fmtFollowers(n?: number): string { if (n == null) return ''; return n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}k` : String(n); }

function CreatorTile({ c, onPick, busy }: { c: CreatorHit; onPick: (c: CreatorHit) => void; busy?: boolean }) {
  const src = c.url.startsWith('/') ? `${API_BASE_URL}${c.url}` : c.url;
  return (
    <button type="button" onClick={() => onPick(c)} title={c.campaign ? `${c.name} · ${c.campaign}` : c.name} className="group flex flex-col items-center gap-1.5 rounded-xl border border-[#eaecf0] bg-white p-3 text-center hover:border-[#be43ff] hover:bg-[#faf5ff]">
      <span className="relative"><img src={src} alt={c.name} decoding="async" referrerPolicy="no-referrer" className="h-16 w-16 rounded-full object-cover ring-2 ring-[#f2f4f7] group-hover:ring-[#e9d5ff]" />{busy && <span className="absolute inset-0 grid place-items-center rounded-full bg-white/70"><Loader2 className="h-5 w-5 animate-spin text-[#be43ff]" /></span>}</span>
      <div className="flex w-full items-center justify-center gap-1 text-[12px] font-semibold text-[#101828]"><span className="truncate">{c.name}</span>{c.verified && <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-[#0ea5e9]" />}</div>
      <div className="w-full truncate text-[11px] text-[#667085]">@{c.handle}{c.followers != null ? ` · ${fmtFollowers(c.followers)}` : ''}{c.platform ? ` · ${c.platform === 'TIKTOK' ? 'TikTok' : c.platform === 'YOUTUBE' ? 'YouTube' : 'Instagram'}` : ''}</div>
    </button>
  );
}

export interface AiPickerItem { id: string; url: string; title: string; prompt?: string; aspect?: SlideMedia['aspect'] }

export function ContentHubPicker({ onPick, onClose, campaignId, aiItems, onGenerateAi, onPickAi, initialMode, onPickCreator }: {
  onPick: (media: SlideMedia, raw: ContentHubMedia) => void;
  onClose: () => void;
  /** Deck's campaign — preselected in the tree so the right content shows first. */
  campaignId?: string;
  /** AI concept visuals already made for this deck — shown under "AI generated". */
  aiItems?: AiPickerItem[];
  /** Makes more visuals (same brief/style as the deck); resolves when they are in `aiItems`. */
  onGenerateAi?: () => Promise<void>;
  onPickAi?: (media: SlideMedia) => void;
  /** Open on a given tab. */
  initialMode?: 'hub' | 'creators' | 'ai' | 'upload';
  /** Creator mode: hand back the creator's facts (creator API) instead of a picture. */
  onPickCreator?: (facts: CreatorFacts) => void;
}) {
  const [mode, setMode] = useState<'hub' | 'creators' | 'ai' | 'upload'>(initialMode || 'hub');
  // Creators tab: profile pictures by handle — your campaign creators instantly, anyone public on Instagram / TikTok via lookup.
  const [cq, setCq] = useState('');
  const [cPlatform, setCPlatform] = useState<'INSTAGRAM' | 'TIKTOK'>('INSTAGRAM');
  const [cBusy, setCBusy] = useState(false);
  const [cErr, setCErr] = useState<string | null>(null);
  const [found, setFound] = useState<CreatorHit[]>([]);
  // Beatly's creator database, searched as you type (handle prefix + name).
  const [dbHits, setDbHits] = useState<CreatorHit[]>([]);
  const [dbBusy, setDbBusy] = useState(false);
  const [storing, setStoring] = useState<string | null>(null);
  // Files from the user's computer, uploaded this session (the deck keeps the URLs once a slide uses them).
  const [uploads, setUploads] = useState<Array<{ url: string; posterUrl?: string; kind: 'image' | 'video'; name: string }>>([]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const abs = (u: string) => (u.startsWith('/') ? `${API_BASE_URL}${u}` : u);
  const uploadFiles = async (list: File[]) => {
    const ok = list.filter((f) => f.type.startsWith('image/') || f.type.startsWith('video/'));
    if (!ok.length) { setUploadErr('Only images and videos.'); return; }
    setUploadBusy(true); setUploadErr(null);
    try { const r = await coworkApi.presentations.uploadMedia(ok); setUploads((u) => [...r.files, ...u]); }
    catch (e) { setUploadErr((e as Error).message); } finally { setUploadBusy(false); }
  };
  const pickUpload = (it: { url: string; posterUrl?: string; kind: 'image' | 'video'; name: string }) => {
    const media: SlideMedia = { url: abs(it.url), posterUrl: it.posterUrl ? abs(it.posterUrl) : undefined, kind: it.kind, aspect: '9:16', frame: 'phone', alt: it.name };
    if (onPickAi) onPickAi(media); else onPick(media, { media_id: it.url, contenthub_url: abs(it.url) } as unknown as ContentHubMedia);
  };
  const pickCreator = (c: CreatorHit) => {
    const media: SlideMedia = { url: abs(c.url), kind: 'image', aspect: '1:1', alt: `${c.name} (@${c.handle})`, caption: `@${c.handle}` };
    if (onPickAi) onPickAi(media); else onPick(media, { media_id: `creator:${c.handle}`, contenthub_url: abs(c.url) } as unknown as ContentHubMedia);
  };
  const dbQuery = cq.trim().replace(/^@/, '');
  useEffect(() => {
    if (mode !== 'creators' || dbQuery.length < 2) { setDbHits([]); return; }
    let live = true; setDbBusy(true);
    const t = setTimeout(async () => {
      try {
        const r = await coworkApi.presentations.searchCreators(dbQuery);
        if (live) setDbHits(r.creators.filter((c) => c.avatarUrl).map((c) => ({ id: c.id, url: c.avatarUrl as string, name: c.name || c.handle, handle: c.handle, platform: c.platform, followers: c.followers ?? undefined, verified: c.verified, source: 'database' })));
      } catch { if (live) setDbHits([]); } finally { if (live) setDbBusy(false); }
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [dbQuery, mode]);
  // Database and campaign avatars are small CDN thumbnails (100–320 px) — the backend copies them into the team's media
  // and swaps in the public HD picture when it can, so the slide gets a crisp photo.
  const pickDbCreator = async (c: CreatorHit) => {
    if (storing) return;
    setStoring(`${c.platform}-${c.handle}`); setCErr(null);
    if (onPickCreator) {
      // Creator-card mode: the card wants facts, not just a picture.
      try {
        const facts = await coworkApi.presentations.creatorFacts({ id: c.id, handle: c.handle, platform: c.platform === 'TIKTOK' ? 'TIKTOK' : 'INSTAGRAM' }).catch(async () => {
          // Not in the database (public lookup) — keep what we know and store the picture.
          const r = await coworkApi.presentations.lookupCreator({ handle: c.handle, platform: c.platform === 'TIKTOK' ? 'TIKTOK' : 'INSTAGRAM', imageUrl: c.url, name: c.name });
          const f: CreatorFacts = { id: '', handle: c.handle, platform: String(c.platform || 'INSTAGRAM'), name: c.name, permalink: '', avatarUrl: r.url, followers: c.followers ?? null, engagementRate: null, gender: null, ageGroup: null, country: null, location: null, language: null, bio: null, interests: [], contentCount: null, audience: null, fetchedAt: new Date().toISOString() };
          return f;
        });
        onPickCreator({ ...facts, avatarUrl: facts.avatarUrl ? abs(facts.avatarUrl) : null });
      } catch (e) { setCErr((e as Error).message); } finally { setStoring(null); }
      return;
    }
    try {
      const r = await coworkApi.presentations.lookupCreator({ handle: c.handle, platform: c.platform === 'TIKTOK' ? 'TIKTOK' : 'INSTAGRAM', imageUrl: c.url, name: c.name });
      pickCreator({ ...c, url: r.url });
    } catch (e) { setCErr((e as Error).message); } finally { setStoring(null); }
  };
  const lookupCreator = async () => {
    const handle = cq.trim().replace(/^@/, '');
    if (!handle || cBusy) return;
    setCBusy(true); setCErr(null);
    try {
      const r = await coworkApi.presentations.lookupCreator({ handle, platform: cPlatform });
      setFound((f) => [{ url: r.url, name: r.name, handle: r.handle, platform: r.platform, followers: r.followers, verified: r.verified, source: 'lookup' }, ...f.filter((x) => !(x.handle === r.handle && x.platform === r.platform))]);
    } catch (e) { setCErr((e as Error).message); } finally { setCBusy(false); }
  };
  const [aiBusy, setAiBusy] = useState(false);
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [selCampaign, setSelCampaign] = useState<string | null>(campaignId || null);
  const [selCreator, setSelCreator] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  // Staff (BO admin token): the client endpoints have no team for us, so the hub tab searches through the
  // back office instead — the selected client by default, or any brand for an example in a proposal.
  const admin = isAdminJwtToken(getTokenFromStorage());
  const scopeTeam = getStaffScopeTeam();
  const [hubTeam, setHubTeam] = useState<string | null>(isClientTeamId(scopeTeam) ? scopeTeam : null);
  const [tq, setTq] = useState('');
  const staffTeams = useQuery({ queryKey: ['cowork', 'staff-scope', 'teams'], queryFn: () => coworkApi.staffScope.teams(), enabled: admin, staleTime: 10 * 60_000 });
  const teamHits = useMemo(() => {
    const list = staffTeams.data?.teams || [];
    const t = tq.trim().toLowerCase();
    return (t ? list.filter((x) => x.name.toLowerCase().includes(t) || x.brands.some((b) => b.toLowerCase().includes(t))) : list).slice(0, 120);
  }, [staffTeams.data, tq]);
  /** Media owned by another client than the one this session works in — an example, not licensed here. */
  const isForeign = (m: ContentHubMedia) => admin && !!m.team_id && m.team_id !== scopeTeam;

  useEffect(() => { const t = setTimeout(() => setQuery(text.trim()), 300); return () => clearTimeout(t); }, [text]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const paths = useQuery({ queryKey: ['contenthub', 'paths'], queryFn: fetchPaths, enabled: !admin, ...CONTENT_HUB_QUERY_PERSISTENCE });
  const tree = useMemo(() => buildTree(paths.data ?? []), [paths.data]);
  const campaignCreators = useMemo<CreatorHit[]>(() => {
    const seen = new Map<string, CreatorHit>();
    for (const b of tree) for (const c of b.campaigns) for (const cr of c.creators) {
      if (!cr.avatarUrl) continue;
      const key = (cr.handle || cr.name).toLowerCase();
      if (!seen.has(key)) seen.set(key, { url: cr.avatarUrl, name: cr.name, handle: cr.handle || cr.name, source: 'campaign', campaign: c.name });
    }
    return [...seen.values()].sort((a, z) => a.name.localeCompare(z.name));
  }, [tree]);
  const cqLower = cq.trim().replace(/^@/, '').toLowerCase();
  const matchedCreators = useMemo(() => cqLower ? campaignCreators.filter((c) => `${c.name} ${c.handle}`.toLowerCase().includes(cqLower)) : campaignCreators, [campaignCreators, cqLower]);

  // Expand the brand and campaign that hold the current selection.
  useEffect(() => {
    if (!tree.length) return;
    setOpen((o) => {
      const next = { ...o };
      for (const b of tree) {
        if (b.campaigns.some((c) => c.id === selCampaign)) { next[b.id] = true; next[selCampaign!] = true; }
      }
      if (tree.length === 1) next[tree[0].id] = true;
      return next;
    });
  }, [tree, selCampaign]);

  // Tree filter: when the user types, keep only branches whose labels match; the API text filter narrows the grid.
  const q = query.toLowerCase();
  const visibleTree = useMemo(() => !q ? tree : tree.map((b) => ({
    ...b,
    campaigns: b.campaigns.map((c) => ({ ...c, creators: c.creators.filter((cr) => `${cr.name} ${cr.handle}`.toLowerCase().includes(q)) }))
      .filter((c) => c.name.toLowerCase().includes(q) || c.creators.length),
  })).filter((b) => b.name.toLowerCase().includes(q) || b.campaigns.length), [tree, q]);

  const gallery = useInfiniteQuery<ContentHubMediaPage, Error, { pages: ContentHubMediaPage[] }, (string | null)[], number>({
    queryKey: ['contenthub', 'picker', admin ? `bo:${hubTeam || '*'}` : 'app', selCampaign, selCreator, query],
    queryFn: ({ pageParam }) => {
      const token = getTokenFromStorage();
      if (!token) throw new Error('Sign in to browse the Content Hub.');
      if (admin) return getBoContentHubSearch(token, { teamId: hubTeam, pageNumber: pageParam, pageSize: PAGE_SIZE, text: query || null, campaignId: selCampaign, collaborationId: selCreator });
      return getContentHubSearch(token, { pageNumber: pageParam, pageSize: PAGE_SIZE, text: query || null, campaignId: selCampaign, collaborationId: selCreator });
    },
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (last.has_more ? pages.length : undefined),
    ...CONTENT_HUB_QUERY_PERSISTENCE,
  });
  const items = useMemo(() => (gallery.data?.pages ?? []).flatMap((p) => p.medias ?? []), [gallery.data]);
  const total = gallery.data?.pages?.[0]?.count_total;

  const select = (campaign: string | null, creator: string | null) => { setMode('hub'); setSelCampaign(campaign); setSelCreator(creator); };
  const selectedLabel = useMemo(() => {
    for (const b of tree) for (const c of b.campaigns) {
      if (selCreator) { const cr = c.creators.find((x) => x.id === selCreator); if (cr) return `${cr.name} · ${c.name}`; }
      else if (c.id === selCampaign) return c.name;
    }
    return 'All content';
  }, [tree, selCampaign, selCreator]);

  const rowCls = (active: boolean) => `flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-[12px] ${active ? 'bg-[#f4ecff] font-semibold text-[#7c3aed]' : 'text-[#344054] hover:bg-[#f2f4f7]'}`;

  return (
    <div role="dialog" aria-modal="true" onClick={onClose} className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 p-6">
      <div onClick={(e) => e.stopPropagation()} className="flex h-[84vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-[#eaecf0] px-5 pb-3 pt-4">
          <div className="flex items-center">
            <div className="text-[16px] font-semibold text-[#101828]">Add media</div>
            <button onClick={onClose} className="ml-auto rounded-md p-1 text-[#667085] hover:bg-[#f2f4f7]" aria-label="Close"><X className="h-4 w-4" /></button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 rounded-xl bg-[#f2f4f7] p-1">
              {([
                ['hub', 'Content Hub', <Folder key="i" className="h-4 w-4" />, typeof total === 'number' ? total : null],
                ['creators', 'Creators', <Users key="i" className="h-4 w-4" />, null],
                ...((aiItems || onGenerateAi) ? [['ai', 'AI generated', <Sparkles key="i" className="h-4 w-4" />, aiItems?.length || 0]] : []),
                ['upload', 'Your computer', <Upload key="i" className="h-4 w-4" />, uploads.length || null],
              ] as Array<[typeof mode, string, ReactNode, number | null]>).map(([m, label, icon, n]) => (
                <button key={m} type="button" onClick={() => setMode(m)} className={`flex items-center gap-2 rounded-lg px-3.5 py-2 text-[13px] font-medium transition ${mode === m ? 'bg-white text-[#101828] shadow-sm' : 'text-[#667085] hover:text-[#101828]'}`}>
                  {icon}{label}{n != null && n > 0 ? <span className="rounded-full bg-[#eaecf0] px-1.5 text-[10.5px] font-semibold text-[#667085]">{n}</span> : null}
                </button>
              ))}
            </div>
            {mode === 'hub' && (
              <div className="relative ml-auto w-full min-w-[220px] sm:w-80">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#98a2b3]" />
                <input autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="Search creator, campaign or keyword" className="w-full rounded-lg border border-[#d0d5dd] py-2 pl-9 pr-3 text-[13px] focus:border-[#be43ff] focus:outline-none" />
              </div>
            )}
          </div>
        </div>

        <div className="flex min-h-0 flex-1">
          {/* Tree: brand → campaign → creator, same source as the Content Hub page. */}
          {/* Staff: clients instead of folders — the working client first, any other brand for an example. */}
          {mode === 'hub' && admin && (
          <aside className="w-64 shrink-0 overflow-y-auto border-r border-[#eaecf0] p-2">
            <input value={tq} onChange={(e) => setTq(e.target.value)} placeholder="Find a client or brand" className="mb-1 w-full rounded-md border border-[#d0d5dd] px-2 py-1 text-[12px] focus:border-[#be43ff] focus:outline-none" />
            <button type="button" onClick={() => { setHubTeam(null); select(null, null); }} className={rowCls(!hubTeam)}><Folder className="h-3.5 w-3.5 shrink-0 opacity-70" /> All brands</button>
            {isClientTeamId(scopeTeam) && staffTeams.data && (
              <button type="button" onClick={() => { setHubTeam(scopeTeam); select(null, null); }} className={rowCls(hubTeam === scopeTeam)}>
                <BadgeCheck className="h-3.5 w-3.5 shrink-0 opacity-70" /> <span className="truncate">{staffTeams.data.teams.find((t) => t.id === scopeTeam)?.name || scopeTeam}</span><span className="ml-auto text-[10px] text-[#98a2b3]">working in</span>
              </button>
            )}
            {staffTeams.isPending && <div className="flex items-center gap-2 px-2 py-3 text-[12px] text-[#98a2b3]"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading clients…</div>}
            {staffTeams.isError && <div className="px-2 py-3 text-[12px] text-[#b42318]">Could not load clients.</div>}
            {teamHits.filter((t) => t.id !== scopeTeam).map((t) => (
              <button key={t.id} type="button" onClick={() => { setHubTeam(t.id); select(null, null); }} className={rowCls(hubTeam === t.id)} title={t.brands.join(', ')}>
                <Folder className="h-3.5 w-3.5 shrink-0 opacity-70" /> <span className="truncate">{t.name}</span>
              </button>
            ))}
          </aside>
          )}
          {mode === 'hub' && !admin && (
          <aside className="w-64 shrink-0 overflow-y-auto border-r border-[#eaecf0] p-2">
            <button type="button" onClick={() => { setMode('hub'); select(null, null); }} className={rowCls(mode === 'hub' && !selCampaign && !selCreator)}><Folder className="h-3.5 w-3.5 shrink-0 opacity-70" /> All content</button>
            {paths.isPending && <div className="flex items-center gap-2 px-2 py-3 text-[12px] text-[#98a2b3]"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading folders…</div>}
            {paths.isError && <div className="px-2 py-3 text-[12px] text-[#b42318]">Could not load folders.</div>}
            {visibleTree.map((b) => {
              const bOpen = open[b.id] ?? !!q;
              return (
                <div key={b.id} className="mt-1">
                  <button type="button" onClick={() => setOpen((o) => ({ ...o, [b.id]: !bOpen }))} className="flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-[12px] font-semibold text-[#101828] hover:bg-[#f2f4f7]">
                    {bOpen ? <ChevronDown className="h-3.5 w-3.5 opacity-60" /> : <ChevronRight className="h-3.5 w-3.5 opacity-60" />}
                    {b.logoUrl ? <img src={b.logoUrl} alt="" className="h-4 w-4 rounded-sm object-cover" /> : null}
                    <span className="truncate">{b.name}</span>
                    <span className="ml-auto text-[10px] font-normal text-[#98a2b3]">{b.campaigns.length}</span>
                  </button>
                  {bOpen && b.campaigns.map((c) => {
                    const cOpen = open[c.id] ?? !!q;
                    const active = selCampaign === c.id && !selCreator;
                    return (
                      <div key={c.id} className="ml-3">
                        <div className="flex items-center">
                          <button type="button" onClick={() => setOpen((o) => ({ ...o, [c.id]: !cOpen }))} className="rounded p-0.5 text-[#98a2b3] hover:bg-[#f2f4f7]" aria-label="Toggle creators">
                            {cOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                          </button>
                          <button type="button" onClick={() => { select(c.id, null); setOpen((o) => ({ ...o, [c.id]: true })); }} className={rowCls(active)} title={c.name}>
                            <span className="truncate">{c.name}</span>
                            {c.year ? <span className="ml-auto text-[10px] font-normal text-[#98a2b3]">{c.year}</span> : null}
                          </button>
                        </div>
                        {cOpen && c.creators.map((cr) => (
                          <button key={cr.id} type="button" onClick={() => select(c.id, cr.id)} className={`ml-6 ${rowCls(selCreator === cr.id)}`} title={cr.handle ? `@${cr.handle}` : cr.name}>
                            {cr.avatarUrl ? <img src={cr.avatarUrl} alt="" className="h-4 w-4 shrink-0 rounded-full object-cover" /> : <span className="h-4 w-4 shrink-0 rounded-full bg-[#e9d5ff]" />}
                            <span className="truncate">{cr.name}</span>
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </aside>
          )}

          {/* Gallery */}
          {mode === 'creators' ? (
            <div className="min-w-0 flex-1 overflow-y-auto p-5">
              <form onSubmit={(e) => { e.preventDefault(); void lookupCreator(); }} className="mb-4 flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#98a2b3]" />
                  <input autoFocus value={cq} onChange={(e) => setCq(e.target.value)} placeholder="@handle or name — searches Beatly's creator database and your campaigns; Look up fetches anyone public" className="w-full rounded-lg border border-[#d0d5dd] py-1.5 pl-8 pr-2 text-[13px]" />
                </div>
                <div className="flex items-center rounded-lg bg-[#f2f4f7] p-0.5">
                  {(['INSTAGRAM', 'TIKTOK'] as const).map((pf) => (
                    <button key={pf} type="button" onClick={() => setCPlatform(pf)} className={`rounded-md px-2 py-1 text-[11.5px] font-medium ${cPlatform === pf ? 'bg-white text-[#101828] shadow-sm' : 'text-[#667085]'}`}>{pf === 'INSTAGRAM' ? 'Instagram' : 'TikTok'}</button>
                  ))}
                </div>
                <button type="submit" disabled={!cqLower || cBusy} className="flex items-center gap-1.5 rounded-lg bg-[#be43ff] px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-[#a835e0] disabled:opacity-50">
                  {cBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} {cBusy ? 'Looking up…' : 'Look up'}
                </button>
              </form>
              {cErr && <div className="mb-3 text-[12px] text-[#b42318]">{cErr}</div>}
              {storing && <div className="mb-3 flex items-center gap-2 text-[12px] text-[#667085]"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {onPickCreator ? 'Fetching profile facts and the sharpest picture…' : 'Fetching the sharpest picture available…'}</div>}
              {onPickCreator && !storing && <div className="mb-3 text-[12px] text-[#667085]">Pick a creator — the card fills itself with their facts from Beatly's creator database.</div>}
              {found.length > 0 && (
                <>
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#98a2b3]">Looked up</div>
                  <div className="mb-5 grid grid-cols-3 gap-3 md:grid-cols-4 lg:grid-cols-6">{found.map((c) => <CreatorTile key={`${c.platform}-${c.handle}`} c={c} busy={storing === `${c.platform}-${c.handle}`} onPick={onPickCreator ? pickDbCreator : pickCreator} />)}</div>
                </>
              )}
              {dbQuery.length >= 2 && (
                <>
                  <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-[#98a2b3]">Beatly creator database{dbBusy && <Loader2 className="h-3 w-3 animate-spin" />}{!dbBusy && dbHits.length ? ` · ${dbHits.length}` : ''}</div>
                  {!dbBusy && dbHits.length === 0 && <div className="mb-4 text-[12.5px] text-[#98a2b3]">Nobody matching “{cq.trim()}” in the database — press Look up to fetch the public profile.</div>}
                  {dbHits.length > 0 && <div className="mb-5 grid grid-cols-3 gap-3 md:grid-cols-4 lg:grid-cols-6">{dbHits.map((c) => <CreatorTile key={`db-${c.platform}-${c.handle}`} c={c} busy={storing === `${c.platform}-${c.handle}`} onPick={pickDbCreator} />)}</div>}
                </>
              )}
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#98a2b3]">In your campaigns{matchedCreators.length ? ` · ${matchedCreators.length}` : ''}</div>
              {paths.isPending && <div className="flex items-center gap-2 py-3 text-[12px] text-[#98a2b3]"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading creators…</div>}
              {!paths.isPending && matchedCreators.length === 0 && <div className="py-6 text-[13px] text-[#98a2b3]">{cqLower ? `No campaign creator matches “${cq.trim()}”.` : 'No creators with a profile picture yet.'}</div>}
              <div className="grid grid-cols-3 gap-3 md:grid-cols-4 lg:grid-cols-6">{matchedCreators.map((c) => <CreatorTile key={`camp-${c.handle}`} c={c} busy={storing === `${c.platform}-${c.handle}`} onPick={pickDbCreator} />)}</div>
            </div>
          ) : mode === 'upload' ? (
            <div className="min-w-0 flex-1 overflow-y-auto p-5" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); void uploadFiles(Array.from(e.dataTransfer.files)); }}>
              <label className={`mb-4 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#d0d5dd] py-8 hover:border-[#be43ff] hover:bg-[#faf5ff] ${uploadBusy ? 'opacity-60' : ''}`}>
                <input type="file" multiple accept="image/*,video/*" className="hidden" onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; void uploadFiles(fs); }} />
                {uploadBusy ? <Loader2 className="h-6 w-6 animate-spin text-[#be43ff]" /> : <Upload className="h-6 w-6 text-[#98a2b3]" />}
                <div className="text-[13px] font-semibold text-[#344054]">{uploadBusy ? 'Uploading…' : 'Drop images or videos, or click to choose'}</div>
                <div className="text-[11.5px] text-[#98a2b3]">JPG, PNG, WebP, GIF, MP4, MOV · up to 250 MB each · shown in a 9:16 phone frame like everything else</div>
                {uploadErr && <div className="text-[11.5px] text-[#b42318]">{uploadErr}</div>}
              </label>
              {uploads.length > 0 && (
                <div className="grid grid-cols-3 gap-3 md:grid-cols-4 lg:grid-cols-5">
                  {uploads.map((it) => (
                    <button key={it.url} type="button" onClick={() => pickUpload(it)} title={it.name} className="group relative aspect-[3/4] overflow-hidden rounded-xl border border-[#eaecf0] bg-[#f2f4f7] text-left hover:border-[#be43ff]">
                      {it.kind === 'video' && !it.posterUrl
                        ? <video src={`${abs(it.url)}#t=0.5`} muted playsInline preload="metadata" className="h-full w-full object-cover" />
                        : <img src={abs(it.posterUrl || it.url)} alt={it.name} decoding="async" className="h-full w-full object-cover" />}
                      {it.kind === 'video' && <span className="absolute left-2 top-2 rounded-full bg-black/60 p-1 text-white"><Film className="h-3 w-3" /></span>}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-[11px] text-white"><div className="truncate font-medium">{it.name}</div></div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : mode === 'ai' ? (
            <div className="min-w-0 flex-1 overflow-y-auto p-5">
              <div className="mb-3 flex items-center gap-3">
                <div className="text-[12px] text-[#667085]">Concept visuals Kai made for this report — captioned “AI concept visual” on the page. Use them for covers, strategy and creative directions, not as creator posts.</div>
                {onGenerateAi && (
                  <button type="button" disabled={aiBusy} onClick={async () => { setAiBusy(true); try { await onGenerateAi(); } finally { setAiBusy(false); } }} className="ml-auto flex shrink-0 items-center gap-1.5 rounded-lg bg-[#be43ff] px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-[#a835e0] disabled:opacity-50">
                    {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {aiBusy ? 'Generating…' : 'Generate more'}
                  </button>
                )}
              </div>
              {!aiItems?.length && !aiBusy && <div className="py-20 text-center text-[13px] text-[#98a2b3]">No AI visuals yet{onGenerateAi ? ' — click “Generate more” to make a set from this report’s brief.' : '.'}</div>}
              <div className="grid grid-cols-3 gap-3 md:grid-cols-4 lg:grid-cols-5">
                {(aiItems || []).map((it) => {
                  const portrait = it.aspect === '9:16' || it.aspect === '4:5';
                  const media: SlideMedia = { url: it.url, kind: 'image', aspect: it.aspect, frame: portrait ? 'phone' : undefined, caption: 'AI concept visual', alt: it.title };
                  return (
                    <button key={it.id} type="button" onClick={() => (onPickAi ? onPickAi(media) : onPick(media, { media_id: it.id, contenthub_url: it.url } as unknown as ContentHubMedia))} title={it.prompt}
                      className={`group relative overflow-hidden rounded-xl border border-[#eaecf0] bg-[#f2f4f7] text-left hover:border-[#be43ff] ${portrait ? 'aspect-[3/4]' : 'aspect-square'}`}>
                      <img src={it.url} alt={it.title} decoding="async" className="h-full w-full object-cover" />
                      <span className="absolute left-2 top-2 rounded-full bg-black/60 p-1 text-white"><Sparkles className="h-3 w-3" /></span>
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-[11px] text-white"><div className="truncate font-medium">{it.title}</div></div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
          <div className="min-w-0 flex-1 overflow-y-auto p-5">
            <div className="mb-3 flex items-baseline gap-2"><div className="text-[13px] font-semibold text-[#101828]">{selectedLabel}</div>{typeof total === 'number' && <div className="text-[12px] text-[#98a2b3]">{total} item{total === 1 ? '' : 's'}</div>}</div>
            {gallery.isError && <div className="text-[13px] text-[#b42318]">{gallery.error.message || 'Could not load the Content Hub'}</div>}
            {!gallery.isError && items.length === 0 && !gallery.isPending && (
              <div className="py-20 text-center text-[13px] text-[#98a2b3]">No media here{query ? ` for "${query}"` : ''}.</div>
            )}
            <div className="grid grid-cols-3 gap-3 md:grid-cols-4 lg:grid-cols-5">
              {items.map((m) => {
                const media = mediaFromContentHub(m);
                const portrait = media.aspect === '9:16' || media.aspect === '4:5';
                return (
                  <button key={m.media_id} type="button" title={media.alt}
                    onClick={() => onPick(isForeign(m) ? { ...media, caption: media.caption || `Example · ${m.brand_name || m.team_name || 'another brand'} (not licensed for this brand)` } : media, m)}
                    className={`group relative overflow-hidden rounded-xl border border-[#eaecf0] bg-[#f2f4f7] text-left hover:border-[#be43ff] ${portrait ? 'aspect-[3/4]' : 'aspect-square'}`}>
                    {media.posterUrl || media.kind === 'image' ? (
                      <img src={media.posterUrl || media.url} alt={media.alt || ''} decoding="async" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[#98a2b3]"><Film className="h-6 w-6" /></div>
                    )}
                    {media.kind === 'video' && <span className="absolute left-2 top-2 rounded-full bg-black/60 p-1 text-white"><Play className="h-3 w-3" /></span>}
                    {isForeign(m) && <span className="absolute right-2 top-2 rounded-full bg-[#fff7e8] px-1.5 py-0.5 text-[10px] font-semibold text-[#7a4b00]" title={`${m.brand_name || m.team_name || 'Another client'} — example only, not licensed for this brand`}>Example</span>}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-[11px] text-white">
                      <div className="truncate font-medium">{m.influencer_name || m.influencer_handle || 'Media'}</div>
                      {m.campaign_name && <div className="truncate opacity-80">{m.campaign_name}</div>}
                    </div>
                  </button>
                );
              })}
            </div>
            {(gallery.isPending || gallery.isFetchingNextPage) && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#667085]" /></div>}
            {gallery.hasNextPage && !gallery.isFetchingNextPage && (
              <div className="flex justify-center py-4">
                <button onClick={() => gallery.fetchNextPage()} className="rounded-lg border border-[#d0d5dd] px-3 py-1.5 text-[12px] font-medium text-[#344054] hover:bg-[#f9fafb]">Load more</button>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
